import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React from "react";

import { allMandatory } from "../../../helpers/allMandatory/allMandatory";
import { ModelState } from "../../../interfaces/modelState";
import { isInsuredComplete } from "../../../providers/dlpFlow/DlpFlow.insured";
import {
  Coverage as CoverageModel,
  CoverageInsuranceType,
  DlpState,
} from "../../../providers/dlpFlow/DlpFlow.interfaces";
import { Accordion } from "../../accordion/Accordion";
import { Button } from "../../buttons/Button";
import styles from "./Coverage.module.scss";
import { Duration } from "./Duration";
import { InsuranceType } from "./InsuranceType";
import { InsuredAmount } from "./InsuredAmount";
import { Insureds } from "./Insureds";

export const getCoverageId = (index: number) => `coverage_${index}`;

interface Props {
  fields: Record<string, Field<string>>;
  data: DlpState;
  index: number;
  coverage: Partial<CoverageModel>;
  modelState: ModelState | null;
  setModelState: (onUpdatedModelState: ModelState) => void;
  onUpdateCoverageEffectiveInsured: (index: number, effectiveInsured: string[]) => void;
  onUpdateCoverageInsuredAmount: (index: number, insuredAmount: number | undefined, hasInsuredAmount: boolean) => void;
  onUpdateCoverageHasInsuredAmount: (index: number) => void;
  onUpdateCoverageDuration: (index: number, duration: number | undefined, hasDuration: boolean) => void;
  onUpdateCoverageHasDuration: (index: number) => void;
  onUpdateCoverageEffectiveDate: (index: number, effectiveDate: string | undefined) => void;
  onUpdateCoverageInsuranceType: (index: number, insuranceType: CoverageInsuranceType) => void;
  onUpdateCoverageReductionRate: (index: number, reductionRate: number | undefined) => void;
  onDeleteCoverage: (index: number) => void;
}

export const Coverage = ({
  onUpdateCoverageEffectiveInsured,
  onUpdateCoverageInsuredAmount,
  onUpdateCoverageHasInsuredAmount,
  onUpdateCoverageDuration,
  onUpdateCoverageHasDuration,
  onUpdateCoverageEffectiveDate,
  onUpdateCoverageInsuranceType,
  onUpdateCoverageReductionRate,
  onDeleteCoverage,
  setModelState,
  modelState,
  coverage,
  fields,
  data,
  index,
}: Props) => {
  const hasSecondInsured = data.insuredPersons.length === 2 && isInsuredComplete(data.insuredPersons[1]);

  const hasInsuredPersons = coverage.effectiveInsured ? coverage.effectiveInsured.length > 0 : false;

  const showInsuredAmount = Boolean(hasInsuredPersons || coverage.hasInsuredAmount);
  const showDuration = Boolean(hasInsuredPersons && coverage.hasInsuredAmount);

  const showInsuranceType = allMandatory(
    hasInsuredPersons,
    coverage.hasInsuredAmount,
    coverage.hasDuration,
    Boolean(coverage.effectiveDate || coverage.hasEffectiveDate)
  );

  const hasMultipleCoverages = data.coverages.length > 1;
  const id = getCoverageId(index);

  const handleDeleteCoverage = () => onDeleteCoverage(index);

  return (
    <Accordion
      startOpen
      title={
        <>
          <Text field={fields.CoverageTitle} /> {index + 1}
        </>
      }
      id={id}
    >
      <>
        {index === 0 && (
          <p className={styles.coverage__infoText}>
            <Text field={fields.CoverageInfo} />
          </p>
        )}

        {hasMultipleCoverages && (
          <div className={styles.coverage__deleteButton}>
            <Button
              id={`${id}_delete_button`}
              type="link"
              onClick={handleDeleteCoverage}
              leading={<i className="icon icon-prullebak" />}
            >
              <Text field={fields.DeleteCoverageText} />
            </Button>
          </div>
        )}

        {hasSecondInsured && (
          <Insureds
            fields={fields}
            name="InsuredSelection"
            fieldName="step1Insureds"
            insureds={coverage.effectiveInsured}
            insuredPersons={data.insuredPersons}
            validationPropertyName={`model.InsuredSelection[${index}]`}
            modelState={modelState}
            setModelState={setModelState}
            index={index}
            onUpdateInsuredPersonsIds={(personIds: string[]) => onUpdateCoverageEffectiveInsured(index, personIds)}
          />
        )}

        <InsuredAmount
          disabled={!showInsuredAmount}
          fields={fields}
          insuredAmount={coverage.insuredAmount}
          validationPropertyName={`model.Coverages[${index}]`}
          modelState={modelState}
          setModelState={setModelState}
          index={index}
          onUpdateCoverageInsuredAmount={onUpdateCoverageInsuredAmount}
          onUpdateCoverageHasInsuredAmount={onUpdateCoverageHasInsuredAmount}
        />

        <Duration
          disabled={!showDuration}
          fields={fields}
          coverage={coverage}
          insuredPersons={data.insuredPersons}
          validationPropertyName={`model.Coverages[${index}]`}
          modelState={modelState}
          setModelState={setModelState}
          index={index}
          onUpdateCoverageDuration={onUpdateCoverageDuration}
          onUpdateCoverageHasDuration={onUpdateCoverageHasDuration}
          onUpdateCoverageEffectiveDate={onUpdateCoverageEffectiveDate}
        />

        <InsuranceType
          disabled={!showInsuranceType}
          fields={fields}
          coverage={coverage}
          validationPropertyName={`model.Coverages[${index}]`}
          modelState={modelState}
          setModelState={setModelState}
          index={index}
          onUpdateCoverageInsuranceType={onUpdateCoverageInsuranceType}
          onUpdateCoverageReductionRate={onUpdateCoverageReductionRate}
        />
      </>
    </Accordion>
  );
};
